import React from 'react';
import { FaEdit, FaTrashAlt, FaCopy, FaExclamationTriangle } from 'react-icons/fa';
import { FaWandMagicSparkles } from 'react-icons/fa6';
import { FormItem, FormQuestionItem } from '../../types';
import { RiDragMove2Fill } from 'react-icons/ri';
import { useLanguage } from '../../LanguageContext';
import { getTranslation } from '../../consts/translations';
import { renderSanitizedHtml } from '../../utils/sanitizeHtml';

interface EditFormTemplateQuestionBoxProps {
  question: FormItem;
  pageNumber?: number;
  isPageBreak?: boolean;
  /** Language names with missing translations; set only on four-language forms. */
  missingLanguages?: string[];
  /** Language names where only child-template text is blank; a warning, never a block. */
  softMissingLanguages?: string[];
  onEdit: (question: FormItem) => void;
  onDelete: (question: FormItem) => void;
  onDuplicate: (question: FormItem) => void;
  renderInputForType: (question: FormItem) => React.ReactNode;
  dragHandleProps?: React.HTMLAttributes<HTMLSpanElement>;
}

const EditFormTemplateQuestionBox: React.FC<EditFormTemplateQuestionBoxProps> = ({
  question,
  pageNumber,
  isPageBreak,
  missingLanguages = [],
  softMissingLanguages = [],
  onEdit,
  onDelete,
  onDuplicate,
  renderInputForType,
  dragHandleProps,
}) => {
  const { language } = useLanguage();
  const translation = getTranslation(language);
  const missingTitle = [
    missingLanguages.length > 0 && `חסר תרגום: ${missingLanguages.join(', ')}`,
    softMissingLanguages.length > 0 && `תבנית שאלת ילד ריקה: ${softMissingLanguages.join(', ')}`,
  ]
    .filter(Boolean)
    .join(' | ');

  if (isPageBreak && pageNumber != null) {
    return (
      <div className='question-box question-box-page-break'>
        <div className='question-actions'>
          {dragHandleProps && (
            <span
              {...dragHandleProps}
              className='drag-handle'
              title={translation.drag_handle_title}
            >
              <RiDragMove2Fill />
            </span>
          )}
          <button
            className='icon-button'
            onClick={() => onDuplicate(question)}
            title={translation.duplicate_button_title}
          >
            <FaCopy />
          </button>
          <button
            className='icon-button'
            onClick={() => onDelete(question)}
            title={translation.delete_button_title}
          >
            <FaTrashAlt />
          </button>
        </div>
        <hr className='page-break-separator' />
        <span className='page-break-label'>
          {translation.page_label} {pageNumber}
        </span>
      </div>
    );
  }

  const q = question as FormQuestionItem;
  const isHidden = q.show_question === false;
  return (
    <div className={`question-box${isHidden ? ' question-box--hidden' : ''}`}>
      <div className='question-actions'>
        {dragHandleProps && (
          <span {...dragHandleProps} className='drag-handle' title={translation.drag_handle_title}>
            <RiDragMove2Fill />
          </span>
        )}
        <button
          className='icon-button'
          onClick={() => onEdit(question)}
          title={translation.edit_button_title}
        >
          <FaEdit />
        </button>
        <button
          className='icon-button'
          onClick={() => onDuplicate(question)}
          title={translation.duplicate_button_title}
        >
          <FaCopy />
        </button>
        <button
          className='icon-button'
          onClick={() => onDelete(question)}
          title={translation.delete_button_title}
        >
          <FaTrashAlt />
        </button>
      </div>
      {pageNumber != null && (
        <span className='page-number-badge' aria-hidden='true'>
          {translation.page_label} {pageNumber}
        </span>
      )}
      <div className='question-meta'>
        <div className='question-key-row'>
          {q.auto_generated_from_parent && (
            <span
              className='auto-generated-badge'
              style={{
                backgroundColor: q.auto_generated_badge_color || '#20c997',
              }}
              title={translation.auto_generated_badge_title}
            >
              <FaWandMagicSparkles size={10} />
            </span>
          )}
          <span className='question-key'>{q.question_key}</span>
          {missingTitle && (
            <span
              className={`missing-translation-badge${missingLanguages.length === 0 ? ' soft' : ''}`}
              title={missingTitle}
              aria-label={missingTitle}
            >
              <FaExclamationTriangle />
            </span>
          )}
          {isHidden && (
            <span className='hidden-question-badge' title={translation.hidden_question_tooltip}>
              {translation.hidden_question_label}
            </span>
          )}
        </div>
        <div className='question-text-inline'>
          {q.mandatory && <span className='question-required'>*</span>}
          <span>{renderSanitizedHtml(q.text)}</span>
        </div>
        {q.description && (
          <div className='question-description'>
            {q.show_description ? (
              renderSanitizedHtml(q.description)
            ) : (
              <s>{renderSanitizedHtml(q.description)}</s>
            )}
          </div>
        )}
      </div>
      <div className='input-container'>{renderInputForType(question)}</div>
    </div>
  );
};

export default EditFormTemplateQuestionBox;
